<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-between align-items-center mb-4 g-3">
      <div class="col-auto">
        <h1 class="mb-0">Finance Dashboard</h1>
        <small class="text-muted">
          {{ appliedFilters.dateFrom }} → {{ appliedFilters.dateTo }}
          <span v-if="appliedFilters.countryCodes.length"
            >· {{ appliedFilters.countryCodes.join(", ") }}</span
          >
          <span v-else>· all countries</span>
        </small>
      </div>
      <div class="col-auto d-flex flex-wrap gap-2 align-items-center">
        <div class="d-flex align-items-center gap-2">
          <label class="form-label mb-0 small" for="financeDateFrom"
            >From</label
          >
          <input
            id="financeDateFrom"
            v-model="dateFrom"
            type="date"
            class="form-control form-control-sm filter-date"
          />
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="form-label mb-0 small" for="financeDateTo">To</label>
          <input
            id="financeDateTo"
            v-model="dateTo"
            type="date"
            class="form-control form-control-sm filter-date"
          />
        </div>
        <div class="filter-countries">
          <MultiSelectComponent
            v-model="selectedCountries"
            :options="countryOptions"
            placeholder="All countries"
            size="sm"
          />
        </div>
        <div
          class="btn-group btn-group-sm"
          role="group"
          aria-label="Quick ranges"
        >
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="applyPreset(12)"
          >
            12 months
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="applyPreset(24)"
          >
            24 months
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="applyYearToDate()"
          >
            YTD
          </button>
        </div>
        <button
          class="btn btn-primary btn-sm"
          :disabled="isLoading"
          @click="fetchData"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          {{ isLoading ? "Loading..." : "Apply filters" }}
        </button>
      </div>
    </div>

    <LoadingIndicatorComponent
      v-if="isLoading"
      message="Loading financial data..."
    />

    <div v-else>
      <!-- Summary Cards -->
      <div class="row g-3 mb-4">
        <div class="col-md-4 col-lg">
          <div class="card h-100 border-success">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Gross Revenue</h6>
              <h3 class="card-title text-success mb-0">
                {{ formatMoney(summary.gross_revenue_settled) }} €
              </h3>
              <small class="text-muted">Settled (invoices paid)</small>
              <div
                v-if="summary.gross_revenue_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-success fst-italic">
                  + {{ formatMoney(summary.gross_revenue_pending) }} € pending
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-lg">
          <div class="card h-100 border-danger">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Costs (Paid)</h6>
              <h3 class="card-title text-danger mb-0">
                {{ formatMoney(summary.costs_paid) }} €
                <span
                  v-if="summary.gross_revenue > 0"
                  class="fs-6 text-muted fw-normal"
                >
                  ({{ pct(summary.costs_paid, summary.gross_revenue) }}%)
                </span>
              </h3>
              <small class="text-muted">EAC + Corrections</small>
              <div
                v-if="summary.costs_pending > 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-danger fst-italic">
                  + {{ formatMoney(summary.costs_pending) }} € pending ({{
                    pct(summary.costs_pending, summary.gross_revenue)
                  }}%)
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-lg">
          <div class="card h-100 border-purple">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                Referral
                <span
                  v-if="
                    summary.expected_referral > summary.referral_bonus &&
                    summary.expected_referral > 0
                  "
                  class="badge bg-secondary bg-opacity-25 text-purple ms-1"
                  >est.</span
                >
              </h6>
              <h3 class="card-title text-purple mb-0">
                {{ formatMoney(summary.referral_settled) }} €
                <span
                  v-if="summary.gross_revenue_settled > 0"
                  class="fs-6 text-muted fw-normal"
                >
                  ({{
                    pct(
                      summary.referral_settled,
                      summary.gross_revenue_settled
                    )
                  }}%)
                </span>
              </h3>
              <small class="text-muted">Partner payouts</small>
              <div
                v-if="summary.referral_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-purple fst-italic">
                  + {{ formatMoney(summary.referral_pending) }} € pending
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg">
          <div class="card h-100 border-info">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Net Revenue</h6>
              <h3
                class="card-title mb-0"
                :class="
                  summary.net_revenue_settled >= 0 ? 'text-info' : 'text-danger'
                "
              >
                {{ formatMoney(summary.net_revenue_settled) }} €
                <span
                  v-if="summary.gross_revenue_settled > 0"
                  class="fs-6 text-muted fw-normal"
                >
                  ({{
                    pct(
                      summary.net_revenue_settled,
                      summary.gross_revenue_settled
                    )
                  }}%)
                </span>
              </h3>
              <small class="text-muted">Gross - Costs</small>
              <div
                v-if="summary.net_revenue_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-info fst-italic">
                  {{ summary.net_revenue_pending > 0 ? "+" : "" }}
                  {{ formatMoney(summary.net_revenue_pending) }} € pending
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg">
          <div class="card h-100 border-primary">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                Net Revenue after Referral
              </h6>
              <h3
                class="card-title mb-0"
                :class="
                  summary.net_after_referral_settled >= 0
                    ? 'text-primary'
                    : 'text-danger'
                "
              >
                {{ formatMoney(summary.net_after_referral_settled) }} €
                <span
                  v-if="summary.gross_revenue_settled > 0"
                  class="fs-6 text-muted fw-normal"
                >
                  ({{
                    pct(
                      summary.net_after_referral_settled,
                      summary.gross_revenue_settled
                    )
                  }}%)
                </span>
              </h3>
              <small class="text-muted">Gross - Costs - Referral</small>
              <div
                v-if="summary.net_after_referral_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-primary fst-italic">
                  {{ summary.net_after_referral_pending > 0 ? "+" : "" }}
                  {{ formatMoney(summary.net_after_referral_pending) }} €
                  pending
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recurring revenue & retention headline -->
      <div class="row g-3 mb-4">
        <div class="col-md-6 col-lg-3">
          <button
            type="button"
            class="card h-100 w-100 text-start border-primary kpi-link"
            @click="goToTab('arr')"
          >
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                Total ARR
                <span
                  v-if="latestArr"
                  class="badge bg-light text-muted fw-normal ms-1"
                  >forward ·
                  {{ periodLabel(latestArr.year, latestArr.month) }}</span
                >
              </h6>
              <h3 v-if="latestArr" class="card-title text-primary mb-0">
                {{
                  latestArr.total_arr === null
                    ? "–"
                    : `${formatMoney(latestArr.total_arr)} €`
                }}
              </h3>
              <h3 v-else class="card-title text-muted mb-0">–</h3>
              <small class="text-muted"
                >Gross: spot + forward GMV plus platform fees · net
                {{
                  latestArr?.net_arr_total != null
                    ? formatMoney(latestArr.net_arr_total)
                    : "–"
                }}
                €</small
              >
            </div>
          </button>
        </div>
        <div class="col-md-6 col-lg-3">
          <button
            type="button"
            class="card h-100 w-100 text-start border-info kpi-link"
            @click="goToTab('arr')"
          >
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Net ARR</h6>
              <h3 v-if="latestArr" class="card-title text-info mb-0">
                {{
                  latestArr.net_arr_total === null
                    ? "–"
                    : `${formatMoney(latestArr.net_arr_total)} €`
                }}
                <span
                  v-if="
                    latestArr.total_arr !== null &&
                    latestArr.net_arr_total !== null &&
                    latestArr.total_arr > 0
                  "
                  class="fs-6 text-muted fw-normal"
                  >({{ pct(latestArr.net_arr_total, latestArr.total_arr) }}% of
                  gross)</span
                >
              </h3>
              <h3 v-else class="card-title text-muted mb-0">–</h3>
              <small v-if="latestArr" class="text-muted"
                >GO net
                {{
                  latestArr.go_net_arr === null
                    ? "–"
                    : `${formatMoney(latestArr.go_net_arr)} €`
                }}
                ({{ formatRatio(latestArr.blended_fee_rate) }} blended fee) +
                platform fees</small
              ><small v-else class="text-muted">GO net + platform fees</small>
            </div>
          </button>
        </div>
        <div class="col-md-6 col-lg-3">
          <button
            type="button"
            class="card h-100 w-100 text-start border-success kpi-link"
            @click="goToTab('arr')"
          >
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Platform fees ARR</h6>
              <h3 v-if="latestArr" class="card-title text-success mb-0">
                {{ formatMoney(latestArr.platform_fees_arr) }} €
                <span
                  v-if="
                    latestArr.total_arr !== null &&
                    latestArr.net_arr_total !== null &&
                    latestArr.total_arr > 0
                  "
                  class="fs-6 text-muted fw-normal"
                  >({{
                    pct(latestArr.platform_fees_arr, latestArr.total_arr)
                  }}%)</span
                >
              </h3>
              <h3 v-else class="card-title text-muted mb-0">–</h3>
              <small class="text-muted"
                >Contracted flat fees + usage-fee run-rate</small
              >
            </div>
          </button>
        </div>
        <div class="col-md-6 col-lg-3">
          <button
            type="button"
            class="card h-100 w-100 text-start kpi-link"
            :class="nrrBorderClass"
            @click="goToTab('retention')"
          >
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                Net revenue retention
                <span
                  v-if="latestRetention"
                  class="badge bg-light text-muted fw-normal ms-1"
                  >{{ latestRetention.window_months }}m ·
                  {{
                    periodLabel(latestRetention.year, latestRetention.month)
                  }}</span
                >
              </h6>
              <h3
                v-if="latestRetention"
                class="card-title mb-0"
                :class="nrrTextClass"
              >
                {{ formatRatio(latestRetention.nrr) }}
              </h3>
              <h3 v-else class="card-title text-muted mb-0">–</h3>
              <small v-if="latestRetention" class="text-muted">
                GRR {{ formatRatio(latestRetention.grr) }} ·
                {{ latestRetention.cohort_size }} customers ·
                {{ latestRetention.churned_customers }} churned
              </small>
              <small v-else class="text-muted"
                >Fee revenue kept from last period's customers</small
              >
            </div>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li v-for="tab in TABS" :key="tab.id" class="nav-item">
          <router-link
            class="nav-link"
            :class="{ active: activeTab === tab.id }"
            :to="{
              name: 'AdminFinancialDashboardView',
              query: { tab: tab.id },
            }"
          >
            {{ tab.label }}
          </router-link>
        </li>
        <li class="nav-item">
          <router-link
            class="nav-link"
            :to="{ name: 'AdminTransactionsPerformanceView' }"
          >
            GO Prices & Performance
            <font-awesome-icon icon="external-link-alt" class="ms-1 small" />
          </router-link>
        </li>
      </ul>

      <!-- Monthly Breakdown Tab -->
      <div v-if="activeTab === 'monthly'" class="row g-4">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Monthly breakdown</h5>
              <p class="text-muted small mb-2">
                Gross revenue, costs, net revenue and referral per month. Solid
                = settled, faded = pending. Click a bar or a table row to open
                the month.
              </p>
              <div style="height: 400px">
                <canvas ref="monthlyChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Net Revenue (Before Referral)</h5>
              <p class="text-muted small">Gross Revenue - Costs</p>
              <div style="height: 300px">
                <canvas ref="profitBeforeRefChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Net Revenue (After Referral)</h5>
              <p class="text-muted small">
                Gross Revenue - Costs - Referral Bonus
              </p>
              <div style="height: 300px">
                <canvas ref="profitAfterRefChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Monthly data</h5>
              <DataTable
                :value="monthlyData"
                :rowClass="clickableRow"
                @row-click="(event) => monthDetailModalRef?.open(event.data)"
                :scrollable="monthlyData.length > 14"
                :scrollHeight="monthlyData.length > 14 ? '500px' : undefined"
                :virtualScrollerOptions="
                  monthlyData.length > 14
                    ? { itemSize: 42, numToleratedItems: 20 }
                    : undefined
                "
                sortField="year"
                :sortOrder="-1"
                class="p-datatable-sm border rounded"
              >
                <Column field="period" header="Period" sortable>
                  <template #body="{ data }">
                    {{ data.year }}-{{ String(data.month).padStart(2, "0") }}
                  </template>
                </Column>
                <Column
                  field="gross_revenue_settled"
                  header="Gross Rev"
                  sortable
                >
                  <template #body="{ data }">
                    <span class="text-success">
                      {{ formatMoney(data.gross_revenue_settled) }} €
                    </span>
                    <div
                      v-if="data.gross_revenue_pending !== 0"
                      class="text-success fst-italic small"
                    >
                      + {{ formatMoney(data.gross_revenue_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column field="costs_paid" header="Costs (Paid)" sortable>
                  <template #body="{ data }">
                    <span v-if="data.costs_paid > 0" class="text-danger">
                      {{ formatMoney(data.costs_paid) }} €
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </Column>
                <Column field="costs_pending" header="Pending Costs" sortable>
                  <template #body="{ data }">
                    <span
                      v-if="data.costs_pending > 0"
                      class="text-danger fst-italic"
                    >
                      {{ formatMoney(data.costs_pending) }} €
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </Column>
                <Column field="referral_settled" header="Referral" sortable>
                  <template #body="{ data }">
                    <span class="text-purple">
                      {{ formatMoney(data.referral_settled) }} €
                      <span
                        v-if="data.expected_referral > data.referral_bonus"
                        class="badge bg-secondary bg-opacity-25 text-purple"
                        >est.</span
                      >
                    </span>
                    <div
                      v-if="data.referral_pending !== 0"
                      class="text-purple fst-italic small"
                    >
                      + {{ formatMoney(data.referral_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column
                  field="net_revenue_settled"
                  header="Net Revenue"
                  sortable
                >
                  <template #body="{ data }">
                    <span
                      :class="
                        data.net_revenue_settled >= 0
                          ? 'text-info fw-bold'
                          : 'text-danger fw-bold'
                      "
                    >
                      {{ formatMoney(data.net_revenue_settled) }} €
                      <span
                        v-if="data.gross_revenue_settled > 0"
                        class="text-muted fw-normal"
                      >
                        ({{
                          pct(
                            data.net_revenue_settled,
                            data.gross_revenue_settled
                          )
                        }}%)
                      </span>
                    </span>
                    <div
                      v-if="data.net_revenue_pending !== 0"
                      class="text-info fst-italic small"
                    >
                      {{ data.net_revenue_pending > 0 ? "+" : "" }}
                      {{ formatMoney(data.net_revenue_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column
                  field="net_after_referral"
                  header="After Referral"
                  sortable
                >
                  <template #body="{ data }">
                    <span
                      :class="
                        data.net_after_referral_settled >= 0
                          ? 'text-primary fw-bold'
                          : 'text-danger fw-bold'
                      "
                    >
                      {{ formatMoney(data.net_after_referral_settled) }} €
                      <span
                        v-if="data.gross_revenue_settled > 0"
                        class="text-muted fw-normal"
                      >
                        ({{
                          pct(
                            data.net_after_referral_settled,
                            data.gross_revenue_settled
                          )
                        }}%)
                      </span>
                    </span>
                    <div
                      v-if="data.net_after_referral_pending !== 0"
                      class="text-primary fst-italic small"
                    >
                      {{ data.net_after_referral_pending > 0 ? "+" : "" }}
                      {{ formatMoney(data.net_after_referral_pending) }} €
                      pending
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      <!-- Revenue mix tab -->
      <RevenueMixSection
        v-if="activeTab === 'mix'"
        :monthly="monthlyData"
        :summary="summary"
        :streams="streams"
        @openMonth="(month) => monthDetailModalRef?.open(month)"
      />

      <!-- ARR tab -->
      <ArrSection
        v-if="activeTab === 'arr'"
        v-model:goPrice="arrGoPrice"
        :data="arr"
        :isLoading="isLoadingArr"
        @openMonth="(row) => arrDetailModalRef?.open(row)"
      />

      <!-- Retention tab -->
      <RetentionSection
        v-if="activeTab === 'retention'"
        v-model:windowMonths="retentionWindow"
        :data="retention"
        :isLoading="isLoadingRetention"
        @openMonth="
          (row, statuses) => retentionDetailModalRef?.open(row, statuses)
        "
      />

      <!-- Country Breakdown Tab -->
      <div v-if="activeTab === 'country'" class="row g-4">
        <div class="col-12">
          <h5 class="mb-0">Buyers · where our sales go</h5>
          <small class="text-muted">
            Keyed on the counterparty's own country, not the registry we deliver
            to. Gross revenue is what buyers pay (GO value plus fees on their
            invoice); net is what Soldera keeps.
          </small>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Gross revenue by buyer country</h5>
              <div class="chart-md" :style="barChartStyle(countryData.length)">
                <canvas ref="countryGrossChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Net revenue by buyer country</h5>
              <div class="chart-md" :style="barChartStyle(countryData.length)">
                <canvas ref="countryNetChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">MWh sold by buyer country</h5>
              <div class="chart-md" :style="barChartStyle(countryData.length)">
                <canvas ref="countryMwhChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Buyer countries</h5>
              <p class="text-muted small">
                Click a row to see every sale behind it, with the buyer country
                next to the delivery registry.
              </p>
              <DataTable
                :value="countryData"
                sortField="gross_revenue"
                :sortOrder="-1"
                class="p-datatable-sm border rounded"
                :rowClass="clickableRow"
                @row-click="
                  (event) =>
                    countryDetailModalRef?.open('buyer', event.data.country)
                "
              >
                <Column field="country" header="Buyer country" sortable />
                <Column field="mwh_sold" header="MWh sold" sortable>
                  <template #body="{ data }">{{
                    data.mwh_sold.toLocaleString()
                  }}</template>
                </Column>
                <Column
                  field="gross_revenue_settled"
                  header="Gross revenue"
                  sortable
                >
                  <template #body="{ data }">
                    <span class="text-success"
                      >{{ formatMoney(data.gross_revenue_settled) }} €</span
                    >
                    <div
                      v-if="data.gross_revenue_pending !== 0"
                      class="text-success fst-italic small"
                    >
                      + {{ formatMoney(data.gross_revenue_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column field="costs_paid" header="Producer payout" sortable>
                  <template #body="{ data }">
                    <span class="text-danger"
                      >{{ formatMoney(data.costs_paid) }} €</span
                    >
                    <div
                      v-if="data.costs_pending > 0"
                      class="text-danger fst-italic small"
                    >
                      + {{ formatMoney(data.costs_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column
                  field="net_revenue_settled"
                  header="Net revenue"
                  sortable
                >
                  <template #body="{ data }">
                    <span
                      :class="
                        data.net_revenue_settled >= 0
                          ? 'text-info fw-bold'
                          : 'text-danger fw-bold'
                      "
                    >
                      {{ formatMoney(data.net_revenue_settled) }} €
                      <span
                        v-if="data.gross_revenue_settled > 0"
                        class="text-muted fw-normal"
                      >
                        ({{
                          pct(
                            data.net_revenue_settled,
                            data.gross_revenue_settled
                          )
                        }}%)
                      </span>
                    </span>
                    <div
                      v-if="data.net_revenue_pending !== 0"
                      class="text-info fst-italic small"
                    >
                      {{ data.net_revenue_pending > 0 ? "+" : ""
                      }}{{ formatMoney(data.net_revenue_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column field="referral_settled" header="Referral" sortable>
                  <template #body="{ data }">
                    <span class="text-purple"
                      >{{ formatMoney(data.referral_settled) }} €</span
                    >
                    <div
                      v-if="data.referral_pending !== 0"
                      class="text-purple fst-italic small"
                    >
                      + {{ formatMoney(data.referral_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column field="tx_count" header="Transactions" sortable>
                  <template #body="{ data }">
                    {{ data.tx_count }}
                    <span
                      v-if="data.tx_count_pending > 0"
                      class="text-muted small fst-italic"
                      >({{ data.tx_count_pending }} pending)</span
                    >
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>

        <div class="col-12 mt-5">
          <h5 class="mb-0">Supply · where our revenue is sourced</h5>
          <small class="text-muted">
            Keyed on the invoiced company's country: producers, plus buyers
            where they pay a procurement fee. Soldera revenue here is commission
            plus platform fees; the GO payout is the value of the certificates
            sourced from that country.
          </small>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Soldera revenue by supply country</h5>
              <p class="text-muted small mb-2">
                Commission + platform fees, settled and pending.
              </p>
              <div
                class="chart-md"
                :style="barChartStyle(supplyCountryData.length)"
              >
                <canvas ref="supplyRevenueChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">GO payout by supply country</h5>
              <p class="text-muted small mb-2">
                Value of certificates sourced (paid + pending).
              </p>
              <div
                class="chart-md"
                :style="barChartStyle(supplyCountryData.length)"
              >
                <canvas ref="supplyPayoutChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">MWh sourced by supply country</h5>
              <div
                class="chart-md"
                :style="barChartStyle(supplyCountryData.length)"
              >
                <canvas ref="supplyMwhChartRef"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Supply countries</h5>
              <p class="text-muted small">
                Click a row to see the companies and sales behind it.
              </p>
              <DataTable
                :value="supplyCountryRows"
                sortField="soldera_revenue"
                :sortOrder="-1"
                class="p-datatable-sm border rounded"
                :rowClass="clickableRow"
                @row-click="
                  (event) =>
                    countryDetailModalRef?.open('supply', event.data.country)
                "
              >
                <Column field="country" header="Supply country" sortable />
                <Column field="producer_count" header="Producers" sortable />
                <Column field="mwh_sourced" header="MWh sourced" sortable>
                  <template #body="{ data }">{{
                    data.mwh_sourced.toLocaleString()
                  }}</template>
                </Column>
                <Column field="go_payout_paid" header="GO payout" sortable>
                  <template #body="{ data }">
                    <span class="text-danger"
                      >{{ formatMoney(data.go_payout_paid) }} €</span
                    >
                    <div
                      v-if="data.go_payout_pending > 0"
                      class="text-danger fst-italic small"
                    >
                      + {{ formatMoney(data.go_payout_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column field="commission_settled" header="Commission" sortable>
                  <template #body="{ data }">
                    <span class="text-primary"
                      >{{ formatMoney(data.commission_settled) }} €</span
                    >
                    <div
                      v-if="data.commission_pending > 0"
                      class="text-primary fst-italic small"
                    >
                      + {{ formatMoney(data.commission_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column
                  field="platform_fees_settled"
                  header="Platform fees"
                  sortable
                >
                  <template #body="{ data }">
                    <span class="text-success"
                      >{{ formatMoney(data.platform_fees_settled) }} €</span
                    >
                    <div
                      v-if="data.platform_fees_pending > 0"
                      class="text-success fst-italic small"
                    >
                      + {{ formatMoney(data.platform_fees_pending) }} € pending
                    </div>
                  </template>
                </Column>
                <Column
                  field="soldera_revenue"
                  header="Soldera revenue"
                  sortable
                >
                  <template #body="{ data }">
                    <span class="fw-bold text-info"
                      >{{ formatMoney(data.soldera_revenue) }} €</span
                    >
                    <span
                      v-if="data.go_payout_paid + data.go_payout_pending > 0"
                      class="text-muted small"
                    >
                      ({{
                        pct(
                          data.soldera_revenue,
                          data.go_payout_paid + data.go_payout_pending
                        )
                      }}% of payout)
                    </span>
                  </template>
                </Column>
                <Column field="referral_bonus" header="Referral" sortable>
                  <template #body="{ data }"
                    ><span class="text-purple"
                      >{{ formatMoney(data.referral_bonus) }} €</span
                    ></template
                  >
                </Column>
                <Column field="tx_count" header="Transactions" sortable />
              </DataTable>
            </div>
          </div>
        </div>
      </div>
    </div>

    <MonthDetailModal
      ref="monthDetailModalRef"
      :countryCodes="appliedFilters.countryCodes"
      :streams="streams"
    />
    <RetentionDetailModal
      ref="retentionDetailModalRef"
      :countryCodes="appliedFilters.countryCodes"
    />
    <ArrDetailModal
      ref="arrDetailModalRef"
      :countryCodes="appliedFilters.countryCodes"
      :goPrice="arrGoPrice"
    />
    <CountryDetailModal ref="countryDetailModalRef" :filters="appliedFilters" />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import { useQueryManager } from "@/composables/useQueryManager";
import { SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants";
import type { ChartConfiguration } from "chart.js";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";
import ArrDetailModal from "./ArrDetailModal.vue";
import ArrSection from "./ArrSection.vue";
import CountryDetailModal from "./CountryDetailModal.vue";
import MonthDetailModal from "./MonthDetailModal.vue";
import RetentionDetailModal from "./RetentionDetailModal.vue";
import RetentionSection from "./RetentionSection.vue";
import RevenueMixSection from "./RevenueMixSection.vue";
import { financialDashboardApi } from "./financialDashboardApi";
import {
  formatRatio,
  isoDateYearsAgo,
  isoMonthsAgo,
  isoToday,
  pct,
  periodLabel,
} from "./financialDashboardMetrics";
import type {
  ArrResponse,
  CountryData,
  DashboardFilters,
  MonthlyData,
  RetentionResponse,
  RevenueStreamInfo,
  Summary,
  SupplyCountryData,
} from "./financialDashboardTypes";
import { useChartCanvas } from "./useChartCanvas";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

defineOptions({ name: "FinancialDashboardView" });

// Stable reference: an inline arrow in the template is a new prop every render and makes
// PrimeVue's DataTable re-render itself endlessly.
const clickableRow = () => "cursor-pointer";

type TabId = "monthly" | "mix" | "arr" | "retention" | "country";

const TABS: { id: TabId; label: string }[] = [
  { id: "monthly", label: "Monthly breakdown" },
  { id: "mix", label: "Revenue mix" },
  { id: "arr", label: "ARR" },
  { id: "retention", label: "Retention (NRR)" },
  { id: "country", label: "By country" },
];

const DEFAULT_RETENTION_WINDOW = 12;

const { globalErrorHandler } = useGlobalHandlers();
const route = useRoute();
const { updateQuery } = useQueryManager();

// Live filter inputs; `appliedFilters` is the snapshot the rendered data was fetched with, so
// drill-down modals query exactly what the user is looking at.
const dateFrom = ref(isoDateYearsAgo(1));
const dateTo = ref(isoToday());
const selectedCountries = ref<string[]>([]);
const appliedFilters = ref<DashboardFilters>({
  dateFrom: dateFrom.value,
  dateTo: dateTo.value,
  countryCodes: [],
});
const countryOptions = SUPPORTED_COUNTRIES_WITH_LABELS;

const activeTab = ref<TabId>("monthly");
const isLoading = ref(false);
const isLoadingArr = ref(false);
const isLoadingRetention = ref(false);

const emptyStreams = () =>
  Object.fromEntries(
    [
      "go_commission",
      "go_trading_margin",
      "go_standalone_sales",
      "fee_cancellation",
      "fee_procurement",
      "fee_issuance",
      "fee_transfer",
      "fee_flat",
      "fee_other",
      "one_off_service",
      "corrections",
    ].map((key) => [key, 0])
  ) as Summary["streams_settled"];

const summary = ref<Summary>({
  gross_revenue: 0,
  gross_revenue_settled: 0,
  gross_revenue_pending: 0,
  go_revenue: 0,
  go_revenue_settled: 0,
  go_revenue_pending: 0,
  go_cost_paid: 0,
  go_cost_pending: 0,
  commission: 0,
  registry_fee_revenue: 0,
  other_fees: 0,
  other_income: 0,
  other_income_pending: 0,
  costs_paid: 0,
  costs_pending: 0,
  registry_fees_paid: 0,
  corrections: 0,
  referral_bonus: 0,
  expected_referral: 0,
  referral_settled: 0,
  referral_pending: 0,
  net_revenue: 0,
  net_revenue_settled: 0,
  net_revenue_pending: 0,
  net_after_referral: 0,
  net_after_referral_settled: 0,
  net_after_referral_pending: 0,
  tx_count: 0,
  tx_count_pending: 0,
  streams_settled: emptyStreams(),
  streams_pending: emptyStreams(),
});
const monthlyData = ref<MonthlyData[]>([]);
const countryData = ref<CountryData[]>([]);
const supplyCountryData = ref<SupplyCountryData[]>([]);
const streams = ref<RevenueStreamInfo[]>([]);
const arr = ref<ArrResponse | null>(null);
const retention = ref<RetentionResponse | null>(null);
// GO price assumption for the forward-looking ARR; null lets the backend derive it from recent sales.
const arrGoPrice = ref<number | null>(null);
const retentionWindow = ref(DEFAULT_RETENTION_WINDOW);

const monthDetailModalRef = useTemplateRef<
  InstanceType<typeof MonthDetailModal>
>("monthDetailModalRef");
const retentionDetailModalRef = useTemplateRef<
  InstanceType<typeof RetentionDetailModal>
>("retentionDetailModalRef");
const arrDetailModalRef =
  useTemplateRef<InstanceType<typeof ArrDetailModal>>("arrDetailModalRef");
const countryDetailModalRef = useTemplateRef<
  InstanceType<typeof CountryDetailModal>
>("countryDetailModalRef");

const monthlyChartRef = useTemplateRef<HTMLCanvasElement>("monthlyChartRef");
const profitBeforeRefChartRef = useTemplateRef<HTMLCanvasElement>(
  "profitBeforeRefChartRef"
);
const profitAfterRefChartRef = useTemplateRef<HTMLCanvasElement>(
  "profitAfterRefChartRef"
);
const countryNetChartRef =
  useTemplateRef<HTMLCanvasElement>("countryNetChartRef");
const countryMwhChartRef =
  useTemplateRef<HTMLCanvasElement>("countryMwhChartRef");
const supplyRevenueChartRef = useTemplateRef<HTMLCanvasElement>(
  "supplyRevenueChartRef"
);
const supplyPayoutChartRef = useTemplateRef<HTMLCanvasElement>(
  "supplyPayoutChartRef"
);
const supplyMwhChartRef =
  useTemplateRef<HTMLCanvasElement>("supplyMwhChartRef");
const countryGrossChartRef = useTemplateRef<HTMLCanvasElement>(
  "countryGrossChartRef"
);

const monthlyChart = useChartCanvas(monthlyChartRef);
const profitBeforeRefChart = useChartCanvas(profitBeforeRefChartRef);
const profitAfterRefChart = useChartCanvas(profitAfterRefChartRef);
const countryNetChart = useChartCanvas(countryNetChartRef);
const countryMwhChart = useChartCanvas(countryMwhChartRef);
const supplyRevenueChart = useChartCanvas(supplyRevenueChartRef);
const supplyPayoutChart = useChartCanvas(supplyPayoutChartRef);
const supplyMwhChart = useChartCanvas(supplyMwhChartRef);
const countryGrossChart = useChartCanvas(countryGrossChartRef);

const COLORS = {
  green: "rgba(40, 167, 69, 0.8)",
  blue: "rgba(0, 123, 255, 0.8)",
  orange: "rgba(255, 193, 7, 0.8)",
  red: "rgba(220, 53, 69, 0.8)",
  purple: "rgba(111, 66, 193, 0.8)",
  cyan: "rgba(23, 162, 184, 0.8)",
};

const supplyCountryRows = computed(() =>
  supplyCountryData.value.map((row) => ({
    ...row,
    soldera_revenue:
      row.commission_settled +
      row.commission_pending +
      row.platform_fees_settled +
      row.platform_fees_pending,
  }))
);

const latestArr = computed(() => arr.value?.rows.at(-1) ?? null);
const latestRetention = computed(() => retention.value?.rows.at(-1) ?? null);
const nrrTextClass = computed(() => {
  const nrr = latestRetention.value?.nrr;
  if (nrr === null || nrr === undefined) return "text-secondary";
  return nrr >= 1 ? "text-success" : "text-warning-emphasis";
});
const nrrBorderClass = computed(() =>
  nrrTextClass.value.replace("text-", "border-")
);

const tabFromRoute = (): TabId => {
  const tab = route.query.tab;
  return TABS.some((candidate) => candidate.id === tab)
    ? (tab as TabId)
    : "monthly";
};

onMounted(fetchData);

watch(
  () => route.query.tab,
  () => {
    activeTab.value = tabFromRoute();
  },
  { immediate: true }
);

// v-if swaps the tab canvases out of the DOM; redraw the ones that belong to the view itself
// (the section components manage their own charts).
watch(activeTab, async () => {
  await nextTick();
  renderViewCharts();
});

watch(arrGoPrice, () => fetchArr());
watch(retentionWindow, () => fetchRetention());

function goToTab(tab: TabId) {
  // Keep every other query parameter (only the tab changes).
  updateQuery({ tab });
}
function applyPreset(months: number) {
  dateFrom.value = isoMonthsAgo(months);
  dateTo.value = isoToday();
  fetchData();
}
function applyYearToDate() {
  dateFrom.value = `${new Date().getFullYear()}-01-01`;
  dateTo.value = isoToday();
  fetchData();
}

// Request sequence numbers: a slow response from an earlier filter set must not overwrite the latest.
let dashboardRequest = 0;
let arrRequest = 0;
let retentionRequest = 0;

async function fetchData() {
  const request = ++dashboardRequest;
  isLoading.value = true;
  appliedFilters.value = {
    dateFrom: dateFrom.value,
    dateTo: dateTo.value,
    countryCodes: [...selectedCountries.value],
  };
  try {
    const [response] = await Promise.all([
      financialDashboardApi.getAll(appliedFilters.value),
      fetchArr(),
      fetchRetention(),
    ]);
    if (request !== dashboardRequest) return;
    summary.value = response.data.summary;
    monthlyData.value = response.data.monthly_data;
    countryData.value = response.data.country_data;
    supplyCountryData.value = response.data.supply_country_data;
    streams.value = response.data.streams;
  } catch (error) {
    if (request === dashboardRequest) globalErrorHandler(error);
  } finally {
    if (request === dashboardRequest) {
      // Flip isLoading BEFORE awaiting nextTick so the v-else branch holding the canvases is in
      // the DOM when the charts are drawn.
      isLoading.value = false;
      await nextTick();
      renderViewCharts();
    }
  }
}
async function fetchArr() {
  const request = ++arrRequest;
  isLoadingArr.value = true;
  try {
    const response = await financialDashboardApi.getArr(
      appliedFilters.value,
      arrGoPrice.value
    );
    if (request === arrRequest) arr.value = response.data;
  } catch (error) {
    if (request === arrRequest) globalErrorHandler(error);
  } finally {
    if (request === arrRequest) isLoadingArr.value = false;
  }
}
async function fetchRetention() {
  const request = ++retentionRequest;
  isLoadingRetention.value = true;
  try {
    const response = await financialDashboardApi.getRetention(
      appliedFilters.value,
      retentionWindow.value
    );
    if (request === retentionRequest) retention.value = response.data;
  } catch (error) {
    if (request === retentionRequest) globalErrorHandler(error);
  } finally {
    if (request === retentionRequest) isLoadingRetention.value = false;
  }
}
function renderViewCharts() {
  if (activeTab.value === "monthly") renderMonthlyCharts();
  else if (activeTab.value === "country") renderCountryCharts();
}

const moneyTooltipY = (label: string | undefined, value: number) =>
  `${label}: ${formatMoney(value)} €`;
const monthLabels = () =>
  monthlyData.value.map((d) => periodLabel(d.year, d.month));

function renderMonthlyCharts() {
  monthlyChart.render({
    type: "bar",
    data: {
      labels: monthLabels(),
      datasets: [
        {
          label: "Gross Revenue (settled)",
          data: monthlyData.value.map((d) => d.gross_revenue_settled),
          backgroundColor: COLORS.green,
          stack: "gross",
        },
        {
          label: "Gross Revenue (pending)",
          data: monthlyData.value.map((d) => d.gross_revenue_pending),
          backgroundColor: "rgba(40, 167, 69, 0.35)",
          borderColor: "#28a745",
          borderWidth: 1,
          stack: "gross",
        },
        {
          label: "Costs (paid)",
          data: monthlyData.value.map((d) => d.costs_paid),
          backgroundColor: "#dc3545",
          stack: "breakdown",
        },
        {
          label: "Costs (pending)",
          data: monthlyData.value.map((d) => d.costs_pending),
          backgroundColor: "rgba(220, 53, 69, 0.4)",
          borderColor: "#dc3545",
          borderWidth: 1,
          stack: "breakdown",
        },
        {
          label: "Net Revenue (settled)",
          data: monthlyData.value.map((d) => d.net_revenue_settled),
          backgroundColor: COLORS.cyan,
          stack: "breakdown",
        },
        {
          label: "Net Revenue (pending)",
          data: monthlyData.value.map((d) => d.net_revenue_pending),
          backgroundColor: "rgba(23, 162, 184, 0.35)",
          borderColor: "#17a2b8",
          borderWidth: 1,
          stack: "breakdown",
        },
        {
          label: "Referral (settled)",
          data: monthlyData.value.map((d) => d.referral_settled),
          backgroundColor: COLORS.orange,
          stack: "referral",
        },
        {
          label: "Referral (pending)",
          data: monthlyData.value.map((d) => d.referral_pending),
          backgroundColor: "rgba(255, 193, 7, 0.35)",
          borderColor: "#ffc107",
          borderWidth: 1,
          stack: "referral",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => moneyTooltipY(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          monthDetailModalRef.value?.open(monthlyData.value[elements[0].index]);
      },
    },
  });

  profitBeforeRefChart.render(
    netChartConfig(
      "net_revenue_settled",
      "net_revenue_pending",
      COLORS.blue,
      "rgba(0, 123, 255, 0.35)",
      "#007bff"
    )
  );
  profitAfterRefChart.render(
    netChartConfig(
      "net_after_referral_settled",
      "net_after_referral_pending",
      COLORS.green,
      "rgba(40, 167, 69, 0.35)",
      "#28a745"
    )
  );
}

function netChartConfig(
  settledField: keyof MonthlyData,
  pendingField: keyof MonthlyData,
  solid: string,
  faded: string,
  border: string
): ChartConfiguration {
  return {
    type: "bar",
    data: {
      labels: monthLabels(),
      datasets: [
        {
          label: "Settled",
          data: monthlyData.value.map((d) => Number(d[settledField])),
          backgroundColor: monthlyData.value.map((d) =>
            Number(d[settledField]) >= 0 ? solid : COLORS.red
          ),
          stack: "net",
        },
        {
          label: "Pending",
          data: monthlyData.value.map((d) => Number(d[pendingField])),
          backgroundColor: faded,
          borderColor: border,
          borderWidth: 1,
          stack: "net",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => moneyTooltipY(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          monthDetailModalRef.value?.open(monthlyData.value[elements[0].index]);
      },
    },
  };
}

/** Horizontal bar charts need room for every label; Chart.js would otherwise skip axis labels. */
const barChartStyle = (rows: number) => ({
  height: `${Math.max(320, 40 + rows * 26)}px`,
});

function horizontalBarConfig<T>(
  rows: T[],
  label: string,
  key: (row: T) => string,
  values: (row: T) => number,
  colors: string | string[],
  money = true
): ChartConfiguration {
  return {
    type: "bar",
    data: {
      labels: rows.map(key),
      datasets: [{ label, data: rows.map(values), backgroundColor: colors }],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      scales: { y: { ticks: { autoSkip: false } } },
      plugins: {
        legend: { display: false },
        tooltip: money
          ? {
              callbacks: {
                label: (ctx) => `${formatMoney(ctx.parsed.x ?? 0)} €`,
              },
            }
          : undefined,
      },
    },
  };
}

function renderCountryCharts() {
  const byGross = [...countryData.value].sort(
    (a, b) => b.gross_revenue - a.gross_revenue
  );
  const byNet = [...countryData.value].sort(
    (a, b) => b.net_revenue - a.net_revenue
  );
  const byMwh = [...countryData.value].sort((a, b) => b.mwh_sold - a.mwh_sold);
  countryGrossChart.render(
    horizontalBarConfig(
      byGross,
      "Gross revenue",
      (d) => d.country,
      (d) => d.gross_revenue,
      COLORS.green
    )
  );
  countryNetChart.render(
    horizontalBarConfig(
      byNet,
      "Net revenue",
      (d) => d.country,
      (d) => d.net_revenue,
      byNet.map((d) => (d.net_revenue >= 0 ? COLORS.cyan : COLORS.red))
    )
  );
  countryMwhChart.render(
    horizontalBarConfig(
      byMwh,
      "MWh sold",
      (d) => d.country,
      (d) => d.mwh_sold,
      COLORS.purple,
      false
    )
  );

  const supply = supplyCountryRows.value;
  const byRevenue = [...supply].sort(
    (a, b) => b.soldera_revenue - a.soldera_revenue
  );
  const byPayout = [...supply].sort(
    (a, b) =>
      b.go_payout_paid +
      b.go_payout_pending -
      (a.go_payout_paid + a.go_payout_pending)
  );
  const bySourced = [...supply].sort((a, b) => b.mwh_sourced - a.mwh_sourced);
  supplyRevenueChart.render(
    horizontalBarConfig(
      byRevenue,
      "Soldera revenue",
      (d) => d.country,
      (d) => d.soldera_revenue,
      COLORS.blue
    )
  );
  supplyPayoutChart.render(
    horizontalBarConfig(
      byPayout,
      "GO payout",
      (d) => d.country,
      (d) => d.go_payout_paid + d.go_payout_pending,
      COLORS.orange
    )
  );
  supplyMwhChart.render(
    horizontalBarConfig(
      bySourced,
      "MWh sourced",
      (d) => d.country,
      (d) => d.mwh_sourced,
      COLORS.purple,
      false
    )
  );
}
</script>

<style scoped lang="scss">
.card {
  border-radius: 0.5rem;
}

.cursor-pointer,
:deep(.cursor-pointer) {
  cursor: pointer;
}

.chart-md {
  height: 320px;
}

.filter-date {
  width: 150px;
}

.filter-countries {
  width: 200px;
}

.kpi-link {
  background: var(--bs-card-bg, #fff);
  transition: box-shadow 0.15s ease-in-out;

  &:hover {
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08);
  }
}

.text-purple {
  color: #7c3aed;
}

.border-purple {
  border-color: #7c3aed !important;
}
</style>
